import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import type { ReactNode } from "react";
import appHome from "@/assets/screens/app-home.png";
import appProgressDark from "@/assets/screens/app-progress-dark.png";
import appNutritionLight from "@/assets/screens/app-nutrition-light.png";
import samsungHealth from "@/assets/logos/samsung-health.png";
import googleHealth from "@/assets/logos/google-health.svg";
import appStoreBadge from "@/assets/logos/appstore-badge.svg";
import googlePlayBadge from "@/assets/logos/googleplay-badge.svg";



/** Cœur Apple Santé — fill solide. Rose-rouge Apple, lisible à 16 px. */
const AppleHeart = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="#FB2C55">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

/** Badges de synchronisation santé : les données wearables remontent dans l'app client. */
const HEALTH_APPS: { name: string; src?: string; node?: ReactNode }[] = [
  { name: "Samsung Health", src: samsungHealth },
  { name: "Google Health", src: googleHealth },
  { name: "Apple Santé", node: <AppleHeart /> },
];
const HealthSync = ({ className = "" }: { className?: string }) => (
  <div className={`flex flex-wrap items-center gap-2 ${className}`}>
    <span className="text-[12.5px] font-medium text-[#0e1220]/45">Se synchronise avec</span>
    {HEALTH_APPS.map(({ src, node, name }) => (
      <span key={name} className="inline-flex items-center gap-1.5 rounded-full border border-[#e7e9f1] bg-white px-2.5 py-1 shadow-[0_1px_3px_rgba(16,20,40,0.05)]">
        {src ? <img src={src} alt={name} width={16} height={16} loading="lazy" className="h-4 w-4 object-contain" /> : node}
        <span className="text-[12.5px] font-semibold text-[#0e1220]/80">{name}</span>
      </span>
    ))}
  </div>
);

const screens = [
  { title: "La séance du jour", description: "Un programme à portée de main, des consignes claires et le prochain entraînement prêt à démarrer.", src: appHome, alt: "Accueil client CoachOS avec la séance du jour et le bilan hebdomadaire" },
  { title: "La nutrition au quotidien", description: "Les repas et les repères nutritionnels se retrouvent dans le même espace que le suivi sportif.", src: appNutritionLight, alt: "CoachOS : suivi nutritionnel et activité du client" },
  { title: "Les progrès qui se voient", description: "Des courbes et des repères pour suivre les évolutions et nourrir vos échanges avec chaque client.", src: appProgressDark, alt: "CoachOS : courbes de progression et mesures du client" },
];

export default function MobileApps() {
  const [active, setActive] = useState(0);
  return (
    <section id="apps" className="mobile-experience">
      <div className="container mobile-experience-grid">
        <div className="mobile-experience-copy">
          <p className="story-eyebrow">02 — Côté client</p>
          <h2 className="landing-section-title">Votre coaching.<br /><span>Dans leur poche.</span></h2>
          <p className="mobile-experience-intro">Entre deux rendez-vous, votre accompagnement continue. Une application pour passer à l’action et voir le chemin parcouru.</p>
          <div className="mobile-screen-selector" role="group" aria-label="Explorer l’application client">
            {screens.map((screen, index) => <button key={screen.title} type="button" aria-pressed={active === index} aria-controls="client-app-preview" onClick={() => setActive(index)}>
              <span className="mobile-screen-number">0{index + 1}</span><span><strong>{screen.title}</strong><span className="mobile-screen-description">{screen.description}</span></span><ArrowUpRight size={18} aria-hidden="true" />
            </button>)}
          </div>
          <div className="mobile-store-links">
            <a href="https://apps.apple.com/fr/app/coachos/id6762567651" target="_blank" rel="noopener noreferrer"><img src={appStoreBadge} alt="Télécharger dans l’App Store" /></a>
            <a href="https://play.google.com/store/apps/details?id=com.muscu.coaching&hl=fr" target="_blank" rel="noopener noreferrer"><img src={googlePlayBadge} alt="Disponible sur Google Play" /></a>
          </div>
          <HealthSync className="mt-6" />
        </div>
        <figure id="client-app-preview" className="mobile-experience-visual">
          <div className="mobile-orbit" aria-hidden="true" />
          <div className="single-phone"><img src={screens[active].src} alt={screens[active].alt} loading="lazy" /></div>
          <figcaption><span>COACHOS POUR VOS CLIENTS</span><strong>{screens[active].title}</strong></figcaption>
        </figure>
      </div>
    </section>
  );
}
